// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LegacyManufacturer, { LegacyManufacturerStyles } from "@flip/App/Components/Product/LegacyManufacturer";
import ProductPreviousPurchase from "@flip/App/Components/Product/ProductPreviousPurchase";
import ProductPriceAndActions, {
    ProductPriceAndActionsStyle,
} from "@flip/App/Components/Product/ProductPriceAndActions";
import productDescriptionVariantStyles from "@flip/App/SharedStyle/ProductDescriptionStyles";
import ProductBrand, { ProductBrandStyles } from "@flip/Overrides/Components/ProductBrand";
import ProductPartNumbers from "@flip/Overrides/Components/ProductPartNumbers";
import { parseBoolean } from "@flip/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { ProductAddToListLinkStyles } from "@insite/content-library/Components/ProductAddToListLink";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { ReactNode, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
});

const mapDispatchToProps = {
    updateProductInfo,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    labelsVisible?: boolean;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    extendedStyles?: ProductCarouselProductCondensedStyles;
    condensedActionsTemplate?: (product: ProductModel) => ReactNode;
    showAvailability?: boolean;
    showMinimumOrderQty?: boolean;
    showMultipleSaleQty?: boolean;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductCondensedStyles {
    container?: InjectableCss;
    imageGridItem?: InjectableCss;
    productImageStyles?: ProductImageStyles;
    productDetailsGridItem?: InjectableCss;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    // productPriceWrapper?: InjectableCss;
    // productPriceStyles?: ProductPriceStyles;
    // productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    // productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    // addToCartContainer?: GridContainerProps;
    // qtyGridItem?: GridItemProps;
    // productQuantityOrderedStyles?: TextFieldProps;
    // addToCartButtonGridItem?: GridItemProps;
    // productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    condensedActionsTemplateGridItem?: GridItemProps;
    // XNG-Changes
    topSection?: InjectableCss;
    priceAndActionsWrapper?: InjectableCss;
    legacyManufacturerStyles?: LegacyManufacturerStyles;
    // XNG-Changes: FLIP-801
    ProductPriceAndActionsExtendedStyle?: ProductPriceAndActionsStyle;
}

export const productCarouselProductCondensedStyles: ProductCarouselProductCondensedStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            height: 100%;
        `,
    },
    topSection: {
        css: css`
            display: flex;
            width: 100%;
        `,
    },
    imageGridItem: {
        css: css`
            flex: 0 0 91px;
            max-width: 91px;
            margin: 0 13px 13px 0;
            height: 128px;
        `,
    },
    productImageStyles: {
        img: {
            width: 91,
            height: 91,
            css: css`
                object-fit: contain;
                max-width: 91px;
                max-height: 91px;
                height: auto;
                margin-bottom: 13px;
            `,
        },
    },
    productBrandWrapper: {
        css: css`
            min-height: 19px;
            margin-bottom: 4px;
        `,
    },
    productDetailsGridItem: {
        css: css`
            flex: 1;
            display: flex;
            flex-direction: column;
            margin-bottom: 12px;
            align-self: flex-start;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin-bottom: 20px;
            `,
        },
    },
    // XNG-Changes: FLIP-801
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 0;
                margin-bottom: 8px;
            `,
        },
    },
    // productPriceWrapper: {
    //     css: css`
    //         flex-grow: 1;
    //         margin-bottom: 6px;
    //     `,
    // },
    // productUnitOfMeasureSelectStyles: {
    //     cssOverrides: {
    //         formField: css`
    //             margin-top: 6px;
    //         `,
    //     },
    // },
    // addToCartContainer: {
    //     gap: 10,
    //     css: css`
    //         flex-grow: 0;
    //         margin-bottom: 6px;
    //     `,
    // },
    // qtyGridItem: { width: 3 },
    // productQuantityOrderedStyles: {
    //     cssOverrides: {
    //         formField: css`
    //             margin-top: 0;
    //         `,
    //     },
    // },
    // addToCartButtonGridItem: { width: 9 },
    // productAddToCartButtonStyles: {
    //     css: css`
    //         width: 100%;
    //     `,
    // },
    // productAddToListLinkStyles: {
    //     link: {
    //         typographyProps: { size: 13 },
    //         css: css`
    //             margin-bottom: 12px;
    //         `,
    //     },
    // },
    condensedActionsTemplateGridItem: {
        width: 1,
    },
    // XNG-Changes
    priceAndActionsWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            flex-grow: 1;
        `,
    },
    // XNG-Changes: FLIP-801
    ProductPriceAndActionsExtendedStyle: {
        productPriceStyles: {
            price: {
                priceText: { weight: "bold", size: 18, lineHeight: "26px" },
                unitOfMeasure: {
                    unitOfMeasureText: {
                        size: 15,
                        lineHeight: "17px",
                        css: css`
                            white-space: nowrap;
                        `,
                    },
                },
            },
        },
    },
};

const ProductCarouselProductCondensed = ({
    carouselId,
    product,
    labelsVisible,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    updateProductInfo,
    extendedStyles,
    productInfo,
    condensedActionsTemplate,
    showAvailability = true,
    showMultipleSaleQty = true,
    showMinimumOrderQty = true,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productCarouselProductCondensedStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    // XNG-Change: hide price section when it is empty
    const emptyPriceAndActionsSection =
        !showAvailability &&
        !showAddToList &&
        !showPrice &&
        !showAddToCart &&
        !showMultipleSaleQty &&
        !showMinimumOrderQty;

    // XNG-Change: changes on structure, using StyledWrapper instead of Grid
    return (
        <>
            <ProductContext.Provider value={productContext}>
                <StyledWrapper {...styles.container}>
                    <StyledWrapper {...styles.topSection}>
                        <StyledWrapper {...styles.imageGridItem}>
                            {showImage && (
                                <ProductImage
                                    product={productContext}
                                    extendedStyles={styles.productImageStyles}
                                    showSmallImage={true}
                                />
                            )}
                            <ProductPreviousPurchase isPreviouslyPurchased={product.properties.isPreviouslyPurchased} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.productDetailsGridItem}>
                            <StyledWrapper {...styles.productBrandWrapper}>
                                {showBrand && product.brand && (
                                    <ProductBrand
                                        brand={product.brand}
                                        isAuthorized={parseBoolean(product.properties.isAuthorizedBrand)}
                                        extendedStyles={styles.productBrandStyles}
                                    />
                                )}
                            </StyledWrapper>

                            {showTitle && (
                                <ProductDescription
                                    product={productContext}
                                    extendedStyles={productDescriptionVariantStyles.small}
                                />
                            )}
                            {showPartNumbers && (
                                <ProductPartNumbers
                                    productNumber={product.productNumber}
                                    customerProductNumber={product.customerProductNumber}
                                    manufacturerItem={product.manufacturerItem}
                                    sku={product.detail?.sku}
                                    extendedStyles={styles.productPartNumbersStyles}
                                />
                            )}
                            {/* XNG-Changes */}
                            {showBrand && product.properties?.legacyManufacturer && (
                                <LegacyManufacturer
                                    legacyManufacturer={product.properties.legacyManufacturer}
                                    legacyManufacturerLink={product.properties.legacyManufacturerDetailUrl}
                                    isAuthorized={parseBoolean(
                                        product.properties.isLegacyManufacturerAnAuthorizedSupplier,
                                    )}
                                    extendedStyles={styles.legacyManufacturerStyles}
                                />
                            )}
                            {/* {showPrice && (
                                <StyledWrapper {...styles.productPriceWrapper}>
                                    <VisuallyHidden
                                        as="label"
                                        htmlFor={`${carouselId}-uom`}
                                        id={`${carouselId}-uom-label`}
                                    >
                                        {translate("U/M")}
                                    </VisuallyHidden>
                                    <ProductPrice
                                        showLabel={false}
                                        product={productContext}
                                        extendedStyles={styles.productPriceStyles}
                                    />
                                    {product.unitOfMeasures && (
                                        <ProductUnitOfMeasureSelect
                                            labelOverride=""
                                            extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                            id={`${carouselId}-uom`}
                                        />
                                    )}
                                    <ProductQuantityBreakPricing
                                        extendedStyles={styles.productQuantityBreakPricingStyles}
                                    />
                                </StyledWrapper>
                            )}
                            {showAddToCart && (
                            <GridContainer {...styles.addToCartContainer}>
                                <GridItem {...styles.qtyGridItem}>
                                    <VisuallyHidden
                                        as="label"
                                        htmlFor={`${carouselId}-qty`}
                                        id={`${carouselId}-qty-label`}
                                    >
                                        {translate("QTY")}
                                    </VisuallyHidden>
                                    <ProductQuantityOrdered
                                        labelOverride=""
                                        extendedStyles={styles.productQuantityOrderedStyles}
                                        id={`${carouselId}-qty`}
                                    />
                                </GridItem>
                                <GridItem {...styles.addToCartButtonGridItem}>
                                    <ProductAddToCartButton
                                        data-test-selector="addToCartBtn"
                                        extendedStyles={styles.productAddToCartButtonStyles}
                                    />
                                </GridItem>
                            </GridContainer>
                        )}
                        {showAddToList && (
                            <ProductAddToListLink
                                data-test-selector="addToListLink"
                                extendedStyles={styles.productAddToListLinkStyles}
                            />
                        )} */}
                        </StyledWrapper>
                    </StyledWrapper>
                    {/* // XNG-Changes: FLIP-801, changed its position */}
                    {!emptyPriceAndActionsSection && (
                        <StyledWrapper {...styles.priceAndActionsWrapper}>
                            <ProductPriceAndActions
                                showAvailability={showAvailability}
                                showAddToList={showAddToList}
                                showPrice={showPrice}
                                showAddToCartActions={showAddToCart}
                                showMultipleSaleQty={showMultipleSaleQty}
                                showMinimumOrderQty={showMinimumOrderQty}
                                // XNG-Changes: FLIP-801
                                extendedStyles={styles.ProductPriceAndActionsExtendedStyle}
                            />
                        </StyledWrapper>
                    )}
                    {condensedActionsTemplate ? (
                        <StyledWrapper {...styles.condensedActionsTemplateGridItem}>
                            {condensedActionsTemplate(product)}
                        </StyledWrapper>
                    ) : (
                        []
                    )}
                </StyledWrapper>
            </ProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProductCondensed);
